import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { unlockDraw } from "@/lib/drawLookup";
import { useFilters } from "@/context/FiltersContext";
import { money, fmtDate, entryLink, canonEvent, moneyLabel, passesMoneyFilter, mountainTodayISO } from "@/lib/ui";
import { EntryChips } from "@/components/EntryChips";
import { getChoices, cycleChoice, setPreference, rodeoTouched } from "@/lib/choices";
import { M, mStatusPill, mStatusLabel } from "@/lib/mobileTheme";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Info, PlusCircle, Users, ListOrdered, ArrowLeftRight, ChevronLeft, ChevronRight, ExternalLink, ChevronDown, Globe, Check, Search, X } from "lucide-react";
import { toast } from "sonner";
import MobileViewHeader from "@/components/mobile/MobileViewHeader";
import usePersistentState from "@/lib/usePersistentState";
import { useSheetSize } from "@/lib/useSheetSize";
import { MRodeoActions, MRodeoDialogs } from "@/components/mobile/rodeoDialogs";
import { MobileTray } from "@/components/mobile/MobileTray";

const dotColor = (r) => (r.associations || []).includes("CPRA") ? "#C8000F" : (r.associations || []).includes("PRCA") ? "#3B82F6" : "#A855F7";

const DLG = "bg-[#161B22] text-slate-100 border border-white/10 max-w-lg";
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const mondayOf = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const isISO = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(new Date(s + "T00:00:00").getTime());
const agendaHeader = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

function ModeToggle({ mode, setMode }) {
  return (
    <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg p-0.5" data-testid="m-cal-mode-toggle">
      {[["day", "This day"], ["month", "Whole month"]].map(([v, label]) => (
        <button key={v} onClick={() => setMode(v)} data-testid={`m-cal-mode-${v}`}
          className={`flex-1 text-[12px] font-bold py-1.5 rounded-md transition-colors ${mode === v ? "bg-[#C8000F] text-white" : "text-slate-400 active:bg-white/5"}`}>{label}</button>
      ))}
    </div>
  );
}

function ActBtn({ onClick, icon: Icon, label, testid, primary }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold ${primary ? "bg-[#C8000F] text-white" : "bg-white/5 text-slate-300"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function MobileCalendar() {
  const { assoc, circuits, minMoney, status, country, province, events, displayEvents, rodeos, dateRange, setCustomRange, selectRodeo, selectedRodeoId, focusDate, matches, clearSelection } = useFilters();
  const [choices, setChoices] = useState(getChoices());
  useEffect(() => { const s = () => setChoices(getChoices()); window.addEventListener("rp-choices-changed", s); return () => window.removeEventListener("rp-choices-changed", s); }, []);
  const [dlg, setDlg] = useState(null);
  const [selDateRaw, setSelDateRaw] = usePersistentState("rp_m_cal_seldate", toISO(new Date()));
  // Guard against a corrupted persisted value: an invalid selDate renders "NaN / Invalid Date"
  // everywhere and shiftDays then writes "NaN-NaN-NaN" back to storage, making it permanent.
  const selDate = isISO(selDateRaw) ? selDateRaw : toISO(new Date());
  const setSelDate = (d) => { if (isISO(d)) setSelDateRaw(d); };
  useEffect(() => { if (!isISO(selDateRaw)) setSelDateRaw(toISO(new Date())); }, [selDateRaw]); // self-heal
  const [showMonth, setShowMonth] = usePersistentState("rp_m_cal_monthgrid", false);
  const sheet = useSheetSize("rp_m_cal_tray_v2");
  // "day" = classic single-day agenda; "month" = every rodeo in the shared date range,
  // grouped under date headers (mirrors the Table/Map set). Default to the fuller view.
  const [viewMode, setViewMode] = usePersistentState("rp_m_cal_viewmode", "month");

  // When a rodeo is selected anywhere (Table/Map/Calendar), land the Calendar on its date.
  // The week strip navigates freely, so we honour the rodeo's day even if it's outside the shared range.
  useEffect(() => { if (focusDate) setSelDate(focusDate); }, [focusDate]); // eslint-disable-line
  // When the shared date range changes (a filter change), pull the selected day into it (prefer today).
  useEffect(() => {
    const from = dateRange?.from, to = dateRange?.to || dateRange?.from;
    if (!from) return;
    if (selDate < from || selDate > to) {
      const t = toISO(new Date());
      setSelDate(t >= from && t <= to ? t : from);
    }
  }, [dateRange?.from, dateRange?.to]); // eslint-disable-line

  // Same filters as the shared matcher EXCEPT the date window — the week strip navigates freely.
  const matchNoDate = (r) =>
    (assoc.size === 0 || (r.associations || []).some((a) => assoc.has(a))) &&
    (circuits.size === 0 || circuits.has(r.circuit_name)) &&
    (minMoney <= 0 || passesMoneyFilter(r, displayEvents, minMoney)) &&
    (events.size === 0 || !(r.events || []).length || (r.events || []).some((e) => events.has(canonEvent(e)))) &&
    (!status || r.status === status) &&
    (!country || (r.country || "Canada") === country) &&
    (!province || (r.province || "Unspecified") === province);

  const dayMap = useMemo(() => {
    const mp = {};
    for (const r of (rodeos || []).filter(matchNoDate)) {
      const seen = new Set();
      for (const p of (r.performances || [])) {
        if (p.date && !seen.has(p.date)) { seen.add(p.date); (mp[p.date] = mp[p.date] || []).push(r); }
      }
    }
    return mp;
  }, [rodeos, assoc, circuits, minMoney, events, displayEvents, status, country, province]); // eslint-disable-line

  const selDt = new Date(selDate + "T00:00:00");
  const weekStart = mondayOf(selDt);
  const weekDays = Array.from({ length: 7 }, (_, i) => toISO(addDays(weekStart, i)));
  const todayISO = toISO(new Date());
  // Navigating days/weeks also updates the SHARED range so the Table always matches.
  const setRangeWeek = (dateISO) => { const ws = mondayOf(new Date(dateISO + "T00:00:00")); setCustomRange(toISO(ws), toISO(addDays(ws, 6))); };
  const goDay = (d) => { setSelDate(d); setRangeWeek(d); };
  // Month-agenda navigation: picking a day only scrolls the agenda (and reveals the tray);
  // it must NOT shrink the shared range to a week like the day-view navigation does.
  const navDay = (d) => { setSelDate(d); if (viewMode === "month") { if (sheet.collapsed) sheet.setCollapsed(false); } else { goDay(d); } };
  const navShiftDays = (n) => navDay(toISO(addDays(selDt, n)));
  const navShiftMonth = (n) => { const d = new Date(selDt); d.setMonth(d.getMonth() + n); navDay(toISO(d)); };
  const navToday = () => navDay(todayISO);

  const touch = useRef({ x: 0, y: 0 });
  const onTouchStart = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x, dy = t.clientY - touch.current.y;
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) navShiftDays(dx < 0 ? 7 : -7);
  };

  const selRodeosRaw = dayMap[selDate] || [];
  // Pin the selected rodeo (e.g. from search) to the top of the day's list — and
  // force-include it even if the current filters would hide it.
  const selRodeos = useMemo(() => {
    let list = selRodeosRaw;
    if (selectedRodeoId && !list.some((r) => r.id === selectedRodeoId)) {
      const sel = (rodeos || []).find((r) => r.id === selectedRodeoId);
      const selDay = sel && ((sel.performances || []).map((p) => p.date).filter(Boolean).sort()[0] || sel.start_date);
      if (sel && selDay === selDate) list = [sel, ...list];
    }
    if (!selectedRodeoId) return list;
    const idx = list.findIndex((r) => r.id === selectedRodeoId);
    if (idx <= 0) return list;
    const copy = [...list];
    const [sel] = copy.splice(idx, 1);
    copy.unshift(sel);
    return copy;
  }, [selRodeosRaw, selectedRodeoId, rodeos, selDate]);
  const forcedSel = useMemo(() => {
    if (!selectedRodeoId) return null;
    const r = (rodeos || []).find((x) => x.id === selectedRodeoId);
    return r && !matches(r) ? r : null;
  }, [selectedRodeoId, rodeos, matches]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const searchResults = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return [];
    return (rodeos || []).filter((r) => `${r.name} ${r.city} ${r.state}`.toLowerCase().includes(qq)).slice(0, 15);
  }, [q, rodeos]);
  const goToRodeo = (r) => {
    const d = (r.performances || []).map((p) => p.date).filter(Boolean).sort()[0] || r.start_date;
    if (d) setSelDate(d);
    selectRodeo(r);
    setSearchOpen(false); setQ("");
  };
  const nextDate = useMemo(() => Object.keys(dayMap).filter((d) => d >= selDate).sort()[0], [dayMap, selDate]);

  // MONTH AGENDA: the exact same rodeo set the Table/Map show for the shared date range,
  // grouped under each date. Mirrors MobileTable's `base` src rule (floor at today unless the
  // range is entirely in the past) so the volume matches across all three views.
  const agenda = useMemo(() => {
    const mToday = mountainTodayISO();
    const from = dateRange?.from, to = dateRange?.to;
    const rangeIsPast = !!(to && to < mToday);
    const hasFuture = (r) => (r.performances || []).some((p) => p.date && p.date >= mToday);
    const inRange = (d) => d && (!from || d >= from) && (!to || d <= to);
    const src = (rodeos || []).filter((r) => (rangeIsPast || hasFuture(r)) && matches(r));
    if (selectedRodeoId && !src.some((r) => r.id === selectedRodeoId)) {
      const sel = (rodeos || []).find((r) => r.id === selectedRodeoId);
      if (sel) src.push(sel);
    }
    const groups = {};
    for (const r of src) {
      const day = (r.performances || []).map((p) => p.date).filter(inRange).sort()[0]
        || (r.performances || []).map((p) => p.date).filter(Boolean).sort()[0] || r.start_date;
      if (!day) continue;
      (groups[day] = groups[day] || []).push(r);
    }
    return Object.keys(groups).sort().map((date) => ({ date, rodeos: groups[date] }));
  }, [rodeos, matches, choices, dateRange, selectedRodeoId]); // eslint-disable-line
  const agendaCount = useMemo(() => agenda.reduce((n, g) => n + g.rodeos.length, 0), [agenda]);
  // When the selected day changes (day tap, cross-view select), scroll the agenda to it.
  useEffect(() => {
    if (viewMode !== "month") return;
    const el = document.getElementById(`m-cal-agenda-${selDate}`);
    if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [selDate, viewMode, agendaCount]); // eslint-disable-line

  // When the filtered set changes (e.g. a $ filter is applied) and the selected day has no
  // matching rodeos, auto-land on the first upcoming day that does — so the Calendar mirrors
  // what the Table/Map are showing instead of sitting on an empty "today". Keyed on the set of
  // days-with-rodeos so it does NOT fire while the rider manually browses to an empty day.
  const dayKeysSig = useMemo(() => Object.keys(dayMap).sort().join(","), [dayMap]);
  useEffect(() => {
    if (viewMode !== "day") return;
    const keys = Object.keys(dayMap).sort();
    if (!keys.length) return;
    if ((dayMap[selDate] || []).length) return;
    setSelDate(keys.find((d) => d >= selDate) || keys[0]);
  }, [dayKeysSig]); // eslint-disable-line

  return (
    <div className="flex flex-col bg-[#0C1017] text-slate-100 h-[calc(100dvh_-_var(--hdr)_-_var(--ftr))] transition-[height] duration-200 overflow-hidden" data-testid="mobile-calendar">
      {searchOpen && (
        <div className="fixed inset-0 z-[3000] bg-[#0C1017] flex flex-col" data-testid="m-cal-search-overlay">
          <div className="shrink-0 flex items-center gap-2 p-3 border-b border-white/10 bg-[#0F141B]" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
            <div className="relative flex-1 min-w-0">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a rodeo (any date / association)…" data-testid="m-cal-search-input"
                className="w-full text-[16px] bg-white/5 border border-white/10 text-slate-100 placeholder-slate-500 rounded-lg pl-9 pr-3 py-2.5 focus:outline-none focus:border-[#C8000F]" />
            </div>
            <button onClick={() => { setSearchOpen(false); setQ(""); }} data-testid="m-cal-search-cancel" className="shrink-0 px-3 py-2 text-[14px] font-semibold text-slate-300 active:text-white">Cancel</button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            {q.trim() ? (
              searchResults.length ? searchResults.map((r) => (
                <button key={r.id} data-testid={`m-cal-search-result-${r.id}`} onClick={() => goToRodeo(r)}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 border-b border-white/5 active:bg-white/5">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: dotColor(r) }} />
                  <span className="min-w-0">
                    <span className="block text-[15px] font-semibold text-white truncate">{r.name}</span>
                    <span className="block text-[12px] text-slate-400 truncate">{[r.city, r.state].filter(Boolean).join(", ")}</span>
                  </span>
                </button>
              )) : <div className="px-4 py-6 text-[13px] text-slate-500">No rodeos match “{q}”.</div>
            ) : <div className="px-4 py-6 text-[13px] text-slate-500">Start typing a rodeo name, city or province…</div>}
          </div>
        </div>
      )}
      <MobileViewHeader
        onSearch={() => setSearchOpen(true)} searchLabel="Search rodeos, towns…" searchTestid="m-cal-search-open"
        onPicker={() => setShowMonth((v) => !v)} pickerActive={showMonth} pickerTestid="m-cal-month-toggle">
        <div className="max-lg:landscape:hidden">
        <div className="px-2 pb-2"><ModeToggle mode={viewMode} setMode={setViewMode} /></div>
        {showMonth ? (
          <MonthGrid monthDate={selDt} dayMap={dayMap} selDate={selDate} todayISO={todayISO} onPick={(d) => { navDay(d); setShowMonth(false); }} />
        ) : (
          <div className="flex items-center gap-1 px-2 pb-2" data-testid="m-cal-week-strip">
            <button onClick={() => navShiftDays(-7)} data-testid="m-cal-prev" className="shrink-0 p-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 active:bg-white/10"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex-1 flex gap-1">
            {weekDays.map((d, i) => {
              const dt = new Date(d + "T00:00:00");
              const rs = dayMap[d] || [];
              const isSel = d === selDate, isToday = d === todayISO;
              const dots = [...new Set(rs.map(dotColor))].slice(0, 3);
              return (
                <button key={d} onClick={() => navDay(d)} data-testid={`m-cal-day-${d}`}
                  className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-xl border transition-colors ${isSel ? "bg-[#C8000F] border-[#C8000F]" : isToday ? "bg-white/5 border-[#C8000F]/50" : "bg-transparent border-transparent active:bg-white/5"}`}>
                  <span className={`text-[10px] font-bold uppercase ${isSel ? "text-white/80" : "text-slate-500"}`}>{WD[i][0]}</span>
                  <span className={`text-[16px] font-display font-extrabold leading-none ${isSel ? "text-white" : isToday ? "text-[#C8000F]" : "text-slate-200"}`}>{dt.getDate()}</span>
                  <span className="flex items-center gap-0.5 h-1.5 mt-0.5">
                    {dots.map((c, j) => <span key={j} className="w-1.5 h-1.5 rounded-full" style={{ background: isSel ? "#fff" : c }} />)}
                  </span>
                </button>
              );
            })}
            </div>
            <button onClick={() => navShiftDays(7)} data-testid="m-cal-next" className="shrink-0 p-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 active:bg-white/10"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
        </div>
      </MobileViewHeader>

      <div className="relative flex-1 min-h-0" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {rodeos === null ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        ) : (
          <div className={`absolute inset-0 overflow-y-auto px-3 pt-3 pb-[80px] transition-all duration-300 ${sheet.collapsed ? "" : "max-lg:landscape:pl-[356px]"}`} data-testid="m-calendar-day">
            {forcedSel && (
              <button onClick={clearSelection} data-testid="m-cal-clear-search" className="mb-2 w-full flex items-center justify-center gap-2 text-[12px] font-semibold bg-[#C8000F]/15 text-[#F26D77] px-3 py-2 rounded-lg">
                Showing “{forcedSel.name}” · Back to my list <X className="w-3.5 h-3.5" />
              </button>
            )}
            <div className="flex items-baseline gap-2.5 mb-3">
              <span className={`text-4xl landscape:text-3xl font-display font-extrabold leading-none ${selDate === todayISO ? "text-[#C8000F]" : "text-white"}`}>{selDt.getDate()}</span>
              <div className="leading-tight">
                <div className="text-[15px] font-bold text-slate-100">{selDt.toLocaleDateString("en-US", { weekday: "long" })}{selDate === todayISO && <span className="ml-2 text-[10px] font-extrabold text-[#C8000F] align-middle">TODAY</span>}</div>
                <div className="text-[12px] text-slate-500">{selDt.toLocaleDateString("en-US", { month: "long", day: "numeric" })} · {viewMode === "month" ? `${agendaCount} rodeo${agendaCount === 1 ? "" : "s"} in range` : `${selRodeos.length} rodeo${selRodeos.length === 1 ? "" : "s"}`}</div>
              </div>
            </div>
            <div className="hidden max-lg:landscape:block" data-testid="m-cal-ls-month">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-[13px] font-display font-extrabold text-white">{selDt.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => navShiftMonth(-1)} data-testid="m-cal-ls-prev-month" className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-300 active:bg-white/10"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={navToday} data-testid="m-cal-ls-today" className="px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-200 text-[12px] font-semibold active:bg-white/10">Today</button>
                  <button onClick={() => navShiftMonth(1)} data-testid="m-cal-ls-next-month" className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-300 active:bg-white/10"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mb-2"><ModeToggle mode={viewMode} setMode={setViewMode} /></div>
              <MonthGrid big monthDate={selDt} dayMap={dayMap} selDate={selDate} todayISO={todayISO}
                onPick={(d) => navDay(d)} />
            </div>
          </div>
        )}
        <MobileTray sheet={sheet} title="rodeos" count={viewMode === "month" ? agendaCount : selRodeos.length} testid="m-cal-tray">
          {viewMode === "month" ? (
            agenda.length ? (
              <div className="px-3 pb-4 pt-2 space-y-4" data-testid="m-cal-agenda">
                {agenda.map((g) => (
                  <div key={g.date} id={`m-cal-agenda-${g.date}`} data-testid={`m-cal-agenda-day-${g.date}`} style={{ scrollMarginTop: "8px" }}>
                    <div className="flex items-baseline gap-2 mb-2 px-0.5">
                      <span className={`text-[13px] font-display font-extrabold ${g.date === todayISO ? "text-[#C8000F]" : "text-white"}`}>{agendaHeader(g.date)}</span>
                      {g.date === todayISO && <span className="text-[10px] font-extrabold text-[#C8000F]">TODAY</span>}
                      <span className="text-[11px] text-slate-500">· {g.rodeos.length} rodeo{g.rodeos.length === 1 ? "" : "s"}</span>
                    </div>
                    <div className="space-y-2.5">
                      {g.rodeos.map((r) => <MRodeoCard key={r.id} r={r} ch={choices[r.id] || {}} selected={selectedRodeoId === r.id} onOpen={(x) => selectRodeo(x)} setDlg={setDlg} events={displayEvents} />)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 px-6" data-testid="m-cal-agenda-empty">
                <p className="text-slate-500 text-sm">No rodeos in this date range.</p>
                <p className="text-slate-600 text-[12px] mt-1">Widen the date range or clear your filters to see more.</p>
              </div>
            )
          ) : selRodeos.length ? (
            <div className="px-3 pb-4 pt-2 space-y-2.5" data-testid="m-cal-cards">
              {selRodeos.map((r) => <MRodeoCard key={r.id} r={r} ch={choices[r.id] || {}} selected={selectedRodeoId === r.id} onOpen={(x) => selectRodeo(x)} setDlg={setDlg} events={displayEvents} />)}
            </div>
          ) : (
            <div className="text-center py-12 px-6" data-testid="m-cal-empty">
              <p className="text-slate-500 text-sm">No rodeos on this day.</p>
              {nextDate && <button onClick={() => setSelDate(nextDate)} data-testid="m-cal-jump-next"
                className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#C8000F]">
                Jump to next rodeo ({new Date(nextDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}) <ChevronRight className="w-4 h-4" />
              </button>}
            </div>
          )}
        </MobileTray>
      </div>

      <MRodeoDialogs dlg={dlg} onClose={() => setDlg(null)} />
    </div>
  );
}

function MonthGrid({ monthDate, dayMap, selDate, todayISO, onPick, big }) {
  const y = monthDate.getFullYear(), m = monthDate.getMonth();
  const startPad = (new Date(y, m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(toISO(new Date(y, m, d)));
  return (
    <div className="px-2 pb-3" data-testid="m-cal-month-grid">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["M", "T", "W", "T", "F", "S", "S"].map((w, i) => <div key={i} className="text-center text-[10px] font-bold text-slate-500">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const dt = new Date(d + "T00:00:00");
          const rs = dayMap[d] || [];
          const isSel = d === selDate, isToday = d === todayISO;
          const dots = [...new Set(rs.map(dotColor))].slice(0, 3);
          return (
            <button key={i} onClick={() => onPick(d)} data-testid={`m-cal-grid-${d}`}
              className={`aspect-square landscape:aspect-auto ${big ? "landscape:h-[52px]" : "landscape:h-9"} flex flex-col items-center justify-center gap-0.5 rounded-lg border ${isSel ? "bg-[#C8000F] border-[#C8000F]" : isToday ? "border-[#C8000F]/50 bg-white/5" : rs.length ? "bg-white/5 border-white/10" : "border-transparent"}`}>
              <span className={`${big ? "text-[13px] landscape:text-[15px]" : "text-[13px]"} font-bold ${isSel ? "text-white" : isToday ? "text-[#C8000F]" : rs.length ? "text-slate-200" : "text-slate-600"}`}>{dt.getDate()}</span>
              <span className="flex items-center gap-0.5 h-1">
                {dots.map((c, j) => <span key={j} className="w-1 h-1 rounded-full" style={{ background: isSel ? "#fff" : c }} />)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MRodeoCard({ r, ch = {}, onOpen, setDlg, selected, events }) {
  const st = r.status;
  return (
    <div className={`${M.card} p-4 ${selected ? "ring-2 ring-[#3B82F6] bg-[#12233B]" : ""}`} data-testid={`m-agenda-card-${r.id}`}>
      <button onClick={() => onOpen(r)} data-testid={`m-day-open-${r.id}`} className="text-left block w-full">
        <div className="flex items-start justify-between gap-2">
          <div className="font-display font-bold text-[17px] leading-snug text-white">{r.name}</div>
          <div className="flex items-center gap-1 shrink-0">
            {ch.pref1 && <span className="w-5 h-5 rounded-full bg-[#C8000F] text-white text-[10px] font-extrabold flex items-center justify-center">1</span>}
            {ch.pref2 && <span className="w-5 h-5 rounded-full bg-[#0C1017] text-white text-[10px] font-extrabold flex items-center justify-center">2</span>}
            {Object.values(ch.entered || {}).some(Boolean) && <span className="w-5 h-5 rounded-full bg-[#16A34A] text-white flex items-center justify-center"><Check className="w-3 h-3" strokeWidth={3} /></span>}
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${mStatusPill(st)}`}>{mStatusLabel(st)}</span>
          </div>
        </div>
        <div className="text-[14px] text-[#F26D77] font-medium mt-1">{r.city}, {r.state}</div>
      </button>
      <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
        {(r.associations || []).map((a) => <span key={a} className="text-[10px] font-bold px-2 py-0.5 rounded border border-white/15 text-slate-300">{a}</span>)}
        <span className="text-[13px] font-semibold text-[#5CC46A] ml-1">{moneyLabel(r, events)}</span>
      </div>
      <EntryChips r={r} dark className="mt-2" />
      <div className="mt-3 pt-3 border-t border-white/10">
        <MRodeoActions r={r} setDlg={setDlg} />
      </div>
    </div>
  );
}

function MInfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="rounded-md bg-white/5 px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm text-slate-100 font-medium mt-0.5">{value}</div>
    </div>
  );
}

function MMoneyTable({ title, rows, testid }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="rounded-md border border-white/10 p-3" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-2">{title}</div>
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between text-sm py-1 px-2 rounded bg-white/5">
            <span className="text-slate-300">{r.event}</span>
            <span className="text-white font-semibold">{money(r.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function InfoDialog({ data, onClose }) {
  const [showRaw, setShowRaw] = useState(false);
  const perfs = data?.performances || [];
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="m-info-dialog">
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-white">{data.name}</DialogTitle>
              <DialogDescription className="text-slate-400">How to enter, ground rules & schedule</DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 pt-1" data-testid="m-info-actions">
              <a href={entryLink(data)} target="_blank" rel="noreferrer" data-testid="m-info-enter-link"
                className="flex-1 flex items-center justify-center gap-2 bg-[#C8000F] text-white text-sm font-semibold py-2.5 rounded-lg active:opacity-90">
                <ExternalLink className="w-4 h-4" /> {(data.associations || []).includes("CPRA") ? "Enter on CPRA" : "Enter online"}
              </a>
              {data.website_url && (
                <a href={data.website_url} target="_blank" rel="noreferrer" data-testid="m-info-official-link"
                  className="flex-1 flex items-center justify-center gap-2 bg-white/10 border border-white/15 text-slate-100 text-sm font-semibold py-2.5 rounded-lg active:bg-white/15">
                  <Globe className="w-4 h-4" /> Official page
                </a>
              )}
            </div>
            <div className="max-h-[62vh] overflow-y-auto space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <MInfoRow label="Location" value={[data.city, data.state].filter(Boolean).join(", ")} />
                <MInfoRow label="Associations" value={(data.associations || []).join(", ")} />
                <MInfoRow label="Added money (total)" value={data.added_money ? money(data.added_money) : ""} />
                <MInfoRow label="Arena" value={data.arena} />
                <MInfoRow label="Entries open" value={fmtDate(data.entry_open)} />
                <MInfoRow label="Entries close" value={fmtDate(data.entry_close)} />
                <MInfoRow label="Committee contact" value={data.committee_contact} />
                <MInfoRow label="Rodeo office" value={data.rodeo_office} />
                <MInfoRow label="Stock contractor" value={data.main_stock} />
                <MInfoRow label="Timed-event stock" value={data.timed_stock} />
              </div>
              {(data.entry_info || data.how_to_enter) && (
                <div className="rounded-md border border-white/10 p-3" data-testid="m-info-how-to-enter">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">How to enter</div>
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{data.entry_info || data.how_to_enter}</p>
                </div>
              )}
              <MMoneyTable title="Purse by event" rows={data.purses} testid="m-info-purses" />
              <MMoneyTable title="Entry fees by event" rows={data.entry_fees} testid="m-info-entry-fees" />
              {(data.perf_order || data.slack_order) && (
                <div className="rounded-md bg-white/5 px-3 py-2 text-sm text-slate-300 space-y-1" data-testid="m-info-order">
                  {data.perf_order && <div><span className="font-semibold text-slate-400">Performance order:</span> {data.perf_order}</div>}
                  {data.slack_info && <div><span className="font-semibold text-slate-400">Slack:</span> {data.slack_info}</div>}
                  {data.slack_order && <div><span className="font-semibold text-slate-400">Slack order:</span> {data.slack_order}</div>}
                </div>
              )}
              {data.ground_rules && (
                <div className="rounded-md border border-white/10 p-3" data-testid="m-info-ground-rules">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Ground rules</div>
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{data.ground_rules}</p>
                </div>
              )}
              {perfs.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Performances & slack</div>
                  {perfs.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm py-1.5 px-3 rounded-md bg-white/5">
                      <span className="flex items-center gap-2"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${p.type === "slack" ? "bg-purple-500/20 text-purple-300" : "bg-blue-500/20 text-blue-300"}`}>{p.type === "slack" ? "SLACK" : "PERF"}</span>{p.label}</span>
                      <span className="text-slate-400">{fmtDate(p.date)} · {p.time}</span>
                    </div>
                  ))}
                </div>
              )}
              {data.details_text && (
                <div className="rounded-md border border-white/10" data-testid="m-info-full-details">
                  <button onClick={() => setShowRaw((v) => !v)} data-testid="m-info-full-details-toggle"
                    className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-semibold text-slate-200">
                    <span>Full rodeo details (from CPRA)</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showRaw ? "rotate-180" : ""}`} />
                  </button>
                  {showRaw && (
                    <p className="px-3 pb-3 text-sm text-slate-300 leading-relaxed whitespace-pre-line border-t border-white/10 pt-2">{data.details_text}</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DrawDialog({ data, onClose }) {
  const nav = useNavigate();
  const [draw, setDraw] = useState(null);
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    if (!data) return;
    setDraw(null); setLocked(false);
    unlockDraw(data.id).then((res) => {
      if (!res.ok) { setLocked(true); return; }
      api.get(`/rodeos/${data.id}/draw`).then((r) => setDraw(r.data)).catch(() => setDraw({ real_draw: false, events: [] }));
    });
  }, [data]);
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="m-draw-dialog">
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-white">Draw · {data.name}</DialogTitle>
              <DialogDescription className="text-slate-400">Who's up, by event and performance</DialogDescription>
            </DialogHeader>
            {locked ? (
              <div className="text-center py-8 px-4" data-testid="m-draw-paywall">
                <p className="text-sm text-slate-300">You've used your free draw lookup. Unlock this draw with 1 credit, or subscribe for unlimited.</p>
                <button onClick={() => { onClose(); nav("/choose-plan"); }} data-testid="m-draw-see-plans" className="mt-3 bg-[#C8000F] text-white text-sm font-semibold px-4 py-2.5 rounded-lg">See plans</button>
              </div>
            ) : !draw ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
              : !draw.real_draw || !(draw.events || []).length ? (
                <p className="text-sm text-slate-400 py-4">The draw hasn't been posted yet for this rodeo. Check back closer to the performance dates.</p>
              ) : (
                <div className="space-y-3 max-h-[55vh] overflow-y-auto">
                  {draw.events.map((ev) => (
                    <div key={ev.event} className="rounded-lg border border-white/10 p-3">
                      <div className="text-sm font-semibold mb-2 flex items-center justify-between text-slate-100"><span>{ev.event}</span><span className="text-xs text-slate-500">{ev.total} entered</span></div>
                      <div className="space-y-1">
                        {ev.rows.slice(0, 60).map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-white/5">
                            <span className="text-slate-200"><b>{r.position}.</b> {ev.team ? `${r.header || "?"} / ${r.heeler || "?"}` : r.contestant}</span>
                            <span className="text-slate-400 text-right">{r.performance_label}{r.stock ? ` · ${r.stock}` : ""}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            <button onClick={() => { onClose(); nav(`/rodeo/${data.id}`); }} data-testid="m-draw-open-full"
              className="w-full mt-3 border border-white/15 text-slate-200 text-sm font-semibold py-2.5 rounded-lg">Open full draw sheet</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TradeDialog({ data, onClose }) {
  const nav = useNavigate();
  const [trades, setTrades] = useState(null);
  useEffect(() => {
    if (data) { setTrades(null); api.get(`/trades?rodeo_id=${data.id}`).then((r) => setTrades(r.data)).catch(() => setTrades([])); }
  }, [data]);
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="m-trade-dialog">
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-white">Trade Board · {data.name}</DialogTitle>
              <DialogDescription className="text-slate-400">Open trade requests for this rodeo</DialogDescription>
            </DialogHeader>
            {!trades ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
              : !trades.length ? (
                <p className="text-sm text-slate-400 py-4">No trade requests posted for this rodeo yet. Post one from the Trade List.</p>
              ) : (
                <div className="space-y-2 max-h-[52vh] overflow-y-auto">
                  {trades.map((t) => (
                    <div key={t.id} className="flex items-center justify-between py-2 px-3 rounded-md bg-white/5">
                      <div>
                        <div className="text-sm font-medium text-slate-100"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mr-1.5 ${t.type === "have" ? "bg-[#1E3A22] text-[#8CC152]" : "bg-[#3A2E12] text-[#E8B54B]"}`}>{t.type === "have" ? "HAS" : "WANTS"}</span>{t.event_category}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{t.performance_label} · {t.user_name}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            <button onClick={() => { onClose(); nav("/trades"); }} data-testid="m-trade-open-full"
              className="w-full mt-3 bg-[#C8000F] text-white text-sm font-semibold py-2.5 rounded-lg">Go to Trade List</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EnterDialog({ data, onClose, choices, setChoices }) {
  if (!data) return <Dialog open={false} onOpenChange={onClose}><DialogContent className={DLG} /></Dialog>;
  const ch = choices[data.id] || {};
  const set = (pid, n) => { setChoices(setPreference(data.id, pid, n)); toast.success(`Set ${n === 1 ? "1st" : "2nd"} preference`); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="m-enter-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-white">Enter · {data.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Pick which performance you want — set 1st and 2nd preference.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {data.performances.map((p) => {
            const is1 = ch.pref1 === p.id, is2 = ch.pref2 === p.id;
            return (
              <div key={p.id} className={`flex items-center justify-between py-2 px-3 rounded-lg border ${is1 ? "bg-[#1E3A22] border-[#5FA83B]" : "bg-white/5 border-transparent"}`}>
                <div>
                  <div className="text-sm font-medium text-slate-100 flex items-center gap-2"><span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${p.type === "slack" ? "bg-purple-500/20 text-purple-300" : "bg-blue-500/20 text-blue-300"}`}>{p.type === "slack" ? "SLACK" : "PERF"}</span>{p.label}</div>
                  <div className="text-xs text-slate-400">{fmtDate(p.date)} · {p.time}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => set(p.id, 1)} data-testid={`m-enter-pref1-${p.id}`} className={`text-xs px-2.5 py-1.5 rounded font-semibold ${is1 ? "bg-[#C8000F] text-white" : "bg-white/10 text-slate-300"}`}>1st</button>
                  <button onClick={() => set(p.id, 2)} data-testid={`m-enter-pref2-${p.id}`} className={`text-xs px-2.5 py-1.5 rounded font-semibold ${is2 ? "bg-[#C8000F] text-white" : "bg-white/10 text-slate-300"}`}>2nd</button>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CountDialog({ data, onClose }) {
  const { events: myEvents } = useFilters();
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    if (data) { setCounts(null); api.get(`/rodeos/${data.id}/count`).then((r) => setCounts(r.data)).catch(() => setCounts({})); }
  }, [data]);
  const evList = data ? data.events.filter((ev) => myEvents.size === 0 || myEvents.has(ev)) : [];
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="m-count-dialog">
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-white">Entry Count · {data.name}</DialogTitle>
              <DialogDescription className="text-slate-400">How the field is filling up per performance.</DialogDescription>
            </DialogHeader>
            {!counts ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div> : (
              <div className="space-y-3 max-h-[55vh] overflow-y-auto">
                {data.performances.map((p) => (
                  <div key={p.id} className="rounded-lg border border-white/10 p-3">
                    <div className="text-sm font-semibold mb-2 flex items-center justify-between text-slate-100"><span>{p.label}</span><span className="text-xs text-slate-500">{fmtDate(p.date)}</span></div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {evList.map((ev) => (
                        <div key={ev} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-white/5">
                          <span className="text-slate-400 truncate mr-2">{ev}</span>
                          <span className="text-[#F26D77] font-bold">{counts[p.id]?.[ev] ?? 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
